import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import api from '../services/api';

interface User {
  id: string;
  name: string;
  email: string;
  avatar_url: string;
  isAdmin: boolean;
}

interface AuthState {
  token: string;
  refreshToken: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface AuthContextData {
  user: User;
  signIn(credentials: SignInCredentials): Promise<void>;
  signOut(): void;
  updateUser(user: User): void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    try {
      const token = localStorage.getItem('@AdopetCP:token');
      const refreshToken = localStorage.getItem('@AdopetCP:refresh_token');
      const userStr = localStorage.getItem('@AdopetCP:user');

      if (token && refreshToken && userStr) {
        api.defaults.headers.common.Authorization = `Bearer ${token}`;
        return { token, refreshToken, user: JSON.parse(userStr) };
      }
    } catch {
      // dados inválidos no localStorage
    }
    return {} as AuthState;
  });
  const [loading, setLoading] = useState(false);

  const signIn = useCallback(async ({ email, password }: SignInCredentials) => {
    setLoading(true);

    try {
      const response = await api.post('/sessions', {
        email,
        password,
      });

      const { token, refresh_token, user } = response.data;

      localStorage.setItem('@AdopetCP:token', token);
      localStorage.setItem('@AdopetCP:refresh_token', refresh_token);
      localStorage.setItem('@AdopetCP:user', JSON.stringify(user));

      api.defaults.headers.common.Authorization = `Bearer ${token}`;

      setData({
        token,
        refreshToken: refresh_token,
        user,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@AdopetCP:token');
    localStorage.removeItem('@AdopetCP:refresh_token');
    localStorage.removeItem('@AdopetCP:user');

    delete api.defaults.headers.common.Authorization;

    setData({} as AuthState);
  }, []);

  const updateUser = useCallback(
    (user: User) => {
      localStorage.setItem('@AdopetCP:user', JSON.stringify(user));

      setData({
        token: data.token,
        refreshToken: data.refreshToken,
        user,
      });
    },
    [data.refreshToken, data.token],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        signIn,
        signOut,
        updateUser,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
